import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { cn } from "@/lib/utils";

/** Paths where the back control must stay hidden. */
export const BACK_BUTTON_HIDDEN_PATHS = new Set([
  "/",
  "/connexion",
  "/admin/connexion",
  "/inscription",
  "/beta",
]);

export function shouldShowBackButton(pathname) {
  if (!pathname) return false;
  if (BACK_BUTTON_HIDDEN_PATHS.has(pathname)) return false;
  // Chat has its own back control — global back steals vertical space on mobile.
  if (pathname.startsWith("/messages") || pathname.startsWith("/messagerie")) return false;
  if (pathname.startsWith("/admin")) return false;
  return true;
}

/**
 * Reusable back control: history (-1) or fallback when there is no in-app history.
 */
export function BackButton({
  fallback = "/",
  className = "",
  label,
  testId = "back-button",
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLang();
  const text = label || t("nav.back");

  const handleClick = () => {
    const idx = window.history.state?.idx;
    if (typeof idx === "number" && idx > 0) {
      navigate(-1);
      return;
    }
    if (location.state?.from) {
      navigate(location.state.from);
      return;
    }
    navigate(fallback);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid={testId}
      aria-label={t("nav.back_aria")}
      className={cn(
        "inline-flex h-11 min-h-11 items-center gap-1.5 rounded-lg border border-matpro-border bg-white px-2.5 text-sm font-semibold text-matpro-anthracite shadow-[0_1px_2px_rgba(15,17,21,0.04)] transition-all duration-200 hover:scale-[1.03] hover:bg-matpro-bg active:scale-[0.98] md:h-9 md:min-h-9",
        className,
      )}
    >
      <ArrowLeft className="h-[18px] w-[18px] shrink-0 sm:h-5 sm:w-5" aria-hidden />
      <span className="hidden sm:inline">{text}</span>
    </button>
  );
}
